/**
 * BAR-1564: once auth-service runs on Keycloak, it answers the Firebase routes
 * (`/login`, `/sso/token`, `/refresh`, `/change-password`) with
 * 410 `{"detail": {"error": "moved_to_keycloak"}}`. A tab opened before that
 * switch keeps the Firebase settings config.json gave it at load, so its
 * 50-minute refresh fails and so does its sign-in form. Reloading reads the new
 * config.json, and the tab signs in through Keycloak: the one extra sign-in the
 * switch costs.
 *
 * The analytics dashboard keeps its own copy, because it builds alone
 * (backend/analytics_dashboard_service/frontend/src/movedToKeycloak.ts). Its
 * message repeats MOVED_TO_KEYCLOAK_USER_MESSAGE (apiErrors.ts) word for word,
 * and docs/specs/keycloak-auth.md quotes it: change all three together.
 */

const RELOADED_AT_KEY = 'bark_moved_to_keycloak_reloaded_at';
/** A second 410 this soon after a reload means the reload did not switch the tab. */
const RELOAD_GUARD_MS = 10 * 60 * 1000;

/** True for auth-service's answer on a route Keycloak took over. */
export function isMovedToKeycloak(status: number, body: unknown): boolean {
    if (status !== 410 || typeof body !== 'object' || body === null) return false;
    const detail = (body as { detail?: unknown }).detail;
    return typeof detail === 'object' && detail !== null
        && (detail as { error?: unknown }).error === 'moved_to_keycloak';
}

/**
 * Reloads the page so it reads config.json again, at most once every ten minutes
 * per tab. A frontend whose config.json still says Firebase while auth-service
 * runs on Keycloak would otherwise reload forever; the caller's error stands
 * instead. A stamp from the future (the clock moved back) does not count.
 * Returns whether a reload started.
 */
export function reloadForKeycloak(now: number = Date.now()): boolean {
    try {
        const last = Number(window.sessionStorage.getItem(RELOADED_AT_KEY)) || 0;
        if (last <= now && now - last < RELOAD_GUARD_MS) return false;
        window.sessionStorage.setItem(RELOADED_AT_KEY, String(now));
    } catch {
        // No sessionStorage, so no loop guard: no reload.
        return false;
    }
    window.location.reload();
    return true;
}
